/**
 * @module cercaliagl/control/LayerRegions
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import Control from './Control';
import MapControls from './MapControls';
import WMS from '../layer/WMS';
import VDropDown, { EventType as DropDownEventType } from '../view/VLayerSwitcher';
import { i18n } from '../dictionary';
import Servers from '../servers';
import VControlButton from '../view/VControlButton';
/**
 * @typedef {Object} LayerServers
 * @property {Array.<string>} servers Servers
 * @property {number} layer Layer
 */
/**
 * Options available for regions.<br/>
 * <ul>
 * <li><b>cercaliagl.control.LayerRegionsOptions.NONE</b>: No layer region option. Value: <code>none</code></li>
 * <li><b>cercaliagl.control.LayerRegionsOptions.NEIGHBORHOODS</b>: Layer neighborhoods. Value: <code>neighborhoods</code></li>
 * <li><b>cercaliagl.control.LayerRegionsOptions.POSTALCODES</b>: Layer postal codes. Value: <code>postalcodes</code></li>
 * <li><b>cercaliagl.control.LayerRegionsOptions.MUNICIPALITIES</b>: Layer municipalities. Value: <code>municipalities</code></li>
 * </ul>
 * <br/>
 * <b>Example</b>: Enable postal codes feature layer.<br/>
 * <br/>
 *
 * ```js
 * const map = new cercaliagl.Map({ target: 'map' });
 * const control = map.getControlByName(cercaliagl.control.MapControls.LAYER_REGIONS);
 * control.enableOption(cercaliagl.control.LayerRegionsOptions.POSTALCODES);
 * ```
 * <br/>
 *
 * @type {Object<string, string>}
 * @api
 */
var Options = {
    /**
     * No layer region option
     * @api
     */
    NONE: 'none',
    /**
     * Neighborhoods
     * @api
     */
    NEIGHBORHOODS: 'neighborhoods',
    /**
     * Postal codes
     * @api
     */
    POSTALCODES: 'postalcodes',
    /**
     * Municipalities
     * @api
     */
    MUNICIPALITIES: 'municipalities',
};
/**
 * @classdesc
 * This class add administrative dividers such as: neighborhoods, postal codes and municipalities.
 * You can enable these layers in dropdown UI component.
 * @extends {Control}
 * @api
 */
var LayerRegions = /** @class */ (function (_super) {
    __extends(LayerRegions, _super);
    /**
     * @constructor
     */
    function LayerRegions() {
        var _this = this;
        var controlButton = new VControlButton('layer-regions');
        var element = controlButton.getElement();
        _this = _super.call(this, {
            element: element,
            className: 'cercaliagl.control.LayerRegions',
            name: MapControls.LAYER_REGIONS,
        }) || this;
        /**
         * @private
         * @type {VControlButton}
         */
        _this.controlButton_ = controlButton;
        /**
         * @private
         * @type {VDropDown}
         */
        _this.dropdown_ = new VDropDown();
        _this.dropdown_.setSelected(null);
        /**
         * @private
         * @type {Array.<import("../view/VLayerSwitcher").Item>}
         */
        _this.items_ = [
            { id: Options.NEIGHBORHOODS, value: i18n('neighborhoods') },
            { id: Options.POSTALCODES, value: i18n('postalcodes') },
            { id: Options.MUNICIPALITIES, value: i18n('municipalities') },
        ];
        /**
         * @private
         * @type {WMS|null}
         */
        _this.wmsLayer_ = null;
        _this.initialize_();
        return _this;
    }
    /**
     * @private
     */
    LayerRegions.prototype.initialize_ = function () {
        var _this = this;
        this.controlButton_.setTooltip(i18n('tooltipLayerRegion'));
        this.element_.addEventListener('click', this.handleClick_.bind(this));
        window.addEventListener('resize', function () { return _this.adjustPosition_(_this.dropdown_); });
        this.dropdown_.on(DropDownEventType.SELECTED, function (item) { return _this.enableOption(item.id); });
        this.dropdown_.on(DropDownEventType.DESELECTED, function () { return _this.disable(); });
        this.initializeListItems_();
    };
    /**
     * @private
     */
    LayerRegions.prototype.initializeListItems_ = function () {
        var _this = this;
        this.items_.forEach(function (item) {
            _this.dropdown_.add(item);
        });
    };
    /**
     * @private
     */
    LayerRegions.prototype.handleClick_ = function () {
        if (!this.dropdown_.isVisible()) {
            // Right align to button.
            var container = this.getMap().getContainer();
            if (!this.dropdown_.getElement().parentElement) {
                this.dropdown_.appendTo(container);
            }
            this.adjustPosition_(this.dropdown_);
            this.dropdown_.show();
        }
        else {
            this.dropdown_.hide();
        }
    };
    /**
      * Options available for regions.<br/>
      * <ul>
      * <li><b>cercaliagl.control.LayerRegionsOptions.NONE</b>: No layer region option. Value: <code>none</code></li>
      * <li><b>cercaliagl.control.LayerRegionsOptions.NEIGHBORHOODS</b>: Layer neighborhoods. Value: <code>neighborhoods</code></li>
      * <li><b>cercaliagl.control.LayerRegionsOptions.POSTALCODES</b>: Layer postal codes. Value: <code>postalcodes</code></li>
      * <li><b>cercaliagl.control.LayerRegionsOptions.MUNICIPALITIES</b>: Layer municipalities. Value: <code>municipalities</code></li>
      * </ul>
      * <br/>
      * <b>Example</b>: Enable postal codes feature layer.<br/>
      * <br/>
      *
      * ```js
      * const map = new cercaliagl.Map({ target: 'map' });
      * const control = map.getControlByName(cercaliagl.control.MapControls.LAYER_REGIONS);
      * control.enableOption(cercaliagl.control.LayerRegionsOptions.POSTALCODES);
      * ```
      * <br/>
      *
     * @param {string} option Option to enable. See above
     * @api
     */
    LayerRegions.prototype.enableOption = function (option) {
        this.toggleWMS_(option);
    };
    /**
     * Disable and remove layer.
     * @override
     * @api
     */
    LayerRegions.prototype.disable = function () {
        if (this.wmsLayer_) {
            var cercaliaMap = this.getCercaliaMap();
            cercaliaMap.removeLayer(this.wmsLayer_);
            this.wmsLayer_ = null;
            this.dropdown_.setSelected(null);
        }
        this.dropdown_.hide();
    };
    /**
     * @static
     * @param {string} option Options
     * @return {LayerServers} Layer and servers
     */
    LayerRegions.getLayerAndServersByOption = function (option) {
        if (option === Options.NEIGHBORHOODS) {
            return { servers: Servers.a9, layer: 'a9w' };
        }
        if (option === Options.POSTALCODES) {
            return { servers: Servers.postalcodes, layer: 'pdw' };
        }
        if (option === Options.MUNICIPALITIES) {
            return { servers: Servers.municipalities, layer: 'a8w' };
        }
        return null;
    };
    /**
     * @private
     * @param {boolean} enable Enable or disable WMS layer
     */
    LayerRegions.prototype.toggleWMS_ = function (option) {
        var cercaliaMap = this.getCercaliaMap();
        if (this.wmsLayer_) {
            if (this.wmsLayer_.getLayerId() === option) {
                this.disable();
                return;
            }
            cercaliaMap.removeLayer(this.wmsLayer_);
        }
        var _a = LayerRegions.getLayerAndServersByOption(option), servers = _a.servers, layer = _a.layer;
        this.wmsLayer_ = new WMS({
            layerId: option,
            url: servers,
            params: {
                width: 1024,
                height: 1024,
                layers: layer,
                projection: 'EPSG:3857',
            },
        });
        cercaliaMap.addLayer(this.wmsLayer_);
        this.dropdown_.hide();
    };
    return LayerRegions;
}(Control));
export default LayerRegions;
//# sourceMappingURL=LayerRegions.js.map